<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, MapPin, Play, Music, Disc3, Layers, ListMusic, FolderOpen } from 'lucide-vue-next'
import SpotifyIcon from '@/components/icons/SpotifyIcon.vue'
import { useScenesStore, type Scene } from '@/stores/scenes'
import { useSpotifyNodesStore, type SpotifyNode } from '@/stores/spotifyNodes'
import { useMusicNodesStore, type MusicNode } from '@/stores/musicNodes'
import { usePlaylistNodesStore, type PlaylistNode } from '@/stores/playlistNodes'
import { useGraphContainersStore, type GraphContainer } from '@/stores/graphContainers'
import { useSpotifyStore } from '@/stores/spotify'
import { parseSpotifyUrl } from '@/lib/spotify-utils'

interface SearchResult {
  id: string
  nodeId: string // The actual node ID in the graph (with prefix for audio nodes)
  name: string
  type: 'scene' | 'spotify' | 'music' | 'playlist' | 'container'
  score: number
  item: Scene | SpotifyNode | MusicNode | PlaylistNode | GraphContainer
  canActivate?: boolean // Whether Enter should activate (play) this item
}

const emit = defineEmits<{
  zoomToNode: [nodeId: string]
  activateItem: [result: SearchResult]
}>()

const scenesStore = useScenesStore()
const spotifyNodesStore = useSpotifyNodesStore()
const musicNodesStore = useMusicNodesStore()
const playlistNodesStore = usePlaylistNodesStore()
const graphContainersStore = useGraphContainersStore()
const spotifyStore = useSpotifyStore()

const searchQuery = ref('')
const isFocused = ref(false)
const selectedIndex = ref(0)
const inputRef = ref<InstanceType<typeof Input> | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)

// Filter toggles (playlists are under music filter)
const filters = ref({
  scenes: true,
  spotify: true,
  music: true, // Includes music nodes and playlists
  containers: true
})

// Cache for spotify artwork
const artworkCache = ref<Map<string, string | null>>(new Map())

// Fetch spotify artwork for a node
const fetchArtwork = async (node: SpotifyNode) => {
  if (!node.spotify_url || artworkCache.value.has(node.id)) return
  artworkCache.value.set(node.id, null) // Mark as loading
  
  const urlInfo = parseSpotifyUrl(node.spotify_url)
  if (!urlInfo || !['track', 'playlist', 'album', 'artist'].includes(urlInfo.type)) return
  
  try {
    const data = await spotifyStore.getTrackOrPlaylist(urlInfo.type as 'track' | 'playlist' | 'album' | 'artist', urlInfo.id)
    if (data) {
      let imageUrl: string | null = null
      if (urlInfo.type === 'track' && data.album?.images?.[0]) {
        imageUrl = data.album.images[data.album.images.length - 1]?.url || data.album.images[0].url // Use smallest image
      } else if (data.images?.[0]) {
        imageUrl = data.images[data.images.length - 1]?.url || data.images[0].url // Use smallest image
      }
      artworkCache.value.set(node.id, imageUrl)
    }
  } catch {
    // Silently fail
  }
}

// Advanced fuzzy match function with word-based matching and fault tolerance
const fuzzyMatch = (query: string, text: string): number => {
  const lowerQuery = query.toLowerCase().trim()
  const lowerText = text.toLowerCase().trim()
  
  // Exact match gets highest score
  if (lowerText === lowerQuery) return 1
  
  // Contains match (full query in text)
  if (lowerText.includes(lowerQuery)) {
    const position = lowerText.indexOf(lowerQuery)
    const positionScore = 1 - (position / lowerText.length) * 0.3
    const lengthScore = lowerQuery.length / lowerText.length
    return 0.7 + (positionScore + lengthScore) * 0.15
  }
  
  // Word-based matching: split query into words and check each
  const queryWords = lowerQuery.split(/\s+/).filter(w => w.length > 0)
  const textWords = lowerText.split(/\s+/).filter(w => w.length > 0)
  
  if (queryWords.length > 1) {
    let wordMatchScore = 0
    let matchedWords = 0
    
    for (const qWord of queryWords) {
      let bestWordScore = 0
      
      // Check if query word matches any text word
      for (const tWord of textWords) {
        // Exact word match
        if (tWord === qWord) {
          bestWordScore = Math.max(bestWordScore, 1)
        }
        // Word starts with query word
        else if (tWord.startsWith(qWord)) {
          bestWordScore = Math.max(bestWordScore, 0.9)
        }
        // Word contains query word
        else if (tWord.includes(qWord)) {
          bestWordScore = Math.max(bestWordScore, 0.7)
        }
        // Fuzzy match within word (for typos)
        else {
          const fuzzyWordScore = fuzzyMatchChars(qWord, tWord)
          if (fuzzyWordScore > 0.5) {
            bestWordScore = Math.max(bestWordScore, fuzzyWordScore * 0.6)
          }
        }
      }
      
      // Also check if the query word matches the full text (for cases like "scene erkrat" matching "Erkrat")
      if (lowerText.includes(qWord)) {
        bestWordScore = Math.max(bestWordScore, 0.85)
      }
      
      if (bestWordScore > 0) {
        matchedWords++
        wordMatchScore += bestWordScore
      }
    }
    
    // All query words should have some match
    if (matchedWords === queryWords.length) {
      return 0.5 + (wordMatchScore / queryWords.length) * 0.4
    } else if (matchedWords > 0) {
      // Partial match - penalize missing words
      return 0.3 * (matchedWords / queryWords.length) * (wordMatchScore / matchedWords)
    }
  }
  
  // Single word or fallback: fuzzy character match
  return fuzzyMatchChars(lowerQuery, lowerText)
}

// Character-by-character fuzzy matching with typo tolerance
const fuzzyMatchChars = (query: string, text: string): number => {
  if (query.length === 0) return 0
  if (text.length === 0) return 0
  
  // Levenshtein-inspired matching with early character skipping allowed
  let queryIndex = 0
  let score = 0
  let consecutiveBonus = 0
  let gaps = 0
  
  for (let i = 0; i < text.length && queryIndex < query.length; i++) {
    if (text[i] === query[queryIndex]) {
      score += 1 + consecutiveBonus * 0.5
      consecutiveBonus++
      queryIndex++
    } else {
      consecutiveBonus = 0
      gaps++
    }
  }
  
  // All characters must match for basic fuzzy
  if (queryIndex === query.length) {
    const gapPenalty = Math.min(gaps * 0.05, 0.3)
    return Math.min(0.6, (score / query.length) * 0.3) - gapPenalty
  }
  
  // Allow 1-2 character typos/missing for longer queries (3+ chars)
  if (query.length >= 3) {
    const missedChars = query.length - queryIndex
    if (missedChars <= Math.ceil(query.length / 4)) {
      // Some tolerance for typos
      const matchRatio = queryIndex / query.length
      return Math.min(0.4, matchRatio * 0.25)
    }
  }
  
  return 0
}

const searchResults = computed<SearchResult[]>(() => {
  if (!searchQuery.value.trim()) return []
  
  const query = searchQuery.value.trim()
  const lowerQuery = query.toLowerCase()
  const results: SearchResult[] = []
  
  // Detect type keywords in query for boosting
  const typeKeywords = {
    scene: ['scene', 'scenes', 'scen', 'scee', 'sene'],
    spotify: ['spotify', 'spot', 'spoti'],
    music: ['music', 'audio', 'sound', 'track', 'song'],
    playlist: ['playlist', 'list', 'plist'],
    container: ['container', 'folder', 'group', 'cont']
  }
  
  // Check which type keywords are present and build a "remaining query" without the type keyword
  let typeBoosts: Record<string, number> = {}
  let searchTerms = lowerQuery
  
  for (const [type, keywords] of Object.entries(typeKeywords)) {
    for (const keyword of keywords) {
      // Check if keyword is a separate word in the query
      const regex = new RegExp(`\\b${keyword}\\b`, 'i')
      if (regex.test(lowerQuery)) {
        typeBoosts[type] = 0.25 // Boost this type
        // Remove the type keyword from search terms
        searchTerms = searchTerms.replace(regex, '').trim()
        break
      }
    }
  }
  
  // Use remaining search terms for matching, or full query if no type keywords
  const matchQuery = searchTerms.length > 0 ? searchTerms : query
  
  // Search scenes (if filter enabled)
  if (filters.value.scenes) {
    for (const scene of scenesStore.scenes.values()) {
      const nameScore = fuzzyMatch(matchQuery, scene.name)
      const descScore = scene.description ? fuzzyMatch(matchQuery, scene.description) * 0.7 : 0
      let score = Math.max(nameScore, descScore)
      
      // Apply type boost
      if (typeBoosts['scene']) {
        score += typeBoosts['scene']
      }
      
      if (score > 0) {
        results.push({
          id: scene.id,
          nodeId: scene.id,
          name: scene.name,
          type: 'scene',
          score,
          item: scene
        })
      }
    }
  }
  
  // Search Spotify nodes (if filter enabled)
  if (filters.value.spotify) {
    for (const node of spotifyNodesStore.allSpotifyNodes) {
      let score = fuzzyMatch(matchQuery, node.name)
      
      // Apply type boost
      if (typeBoosts['spotify']) {
        score += typeBoosts['spotify']
      }
      
      if (score > 0) {
        results.push({
          id: node.id,
          nodeId: `spotify-${node.id}`,
          name: node.name,
          type: 'spotify',
          score,
          item: node
        })
      }
    }
  }
  
  // Search Music nodes (if filter enabled)
  if (filters.value.music) {
    for (const node of musicNodesStore.allMusicNodes) {
      const nameScore = fuzzyMatch(matchQuery, node.name)
      const fileScore = fuzzyMatch(matchQuery, node.file_name) * 0.8
      let score = Math.max(nameScore, fileScore)
      
      // Apply type boost
      if (typeBoosts['music']) {
        score += typeBoosts['music']
      }
    
      if (score > 0) {
        results.push({
          id: node.id,
          nodeId: `music-${node.id}`,
          name: node.name,
          type: 'music',
          score,
          item: node
        })
      }
    }
  }
  
  // Search Playlists (under music filter - they're audio-related)
  if (filters.value.music) {
    for (const playlist of playlistNodesStore.allPlaylistNodes) {
      let score = fuzzyMatch(matchQuery, playlist.name)
      
      // Apply type boost
      if (typeBoosts['playlist']) {
        score += typeBoosts['playlist']
      }
      
      if (score > 0) {
        results.push({
          id: playlist.id,
          nodeId: `playlist-${playlist.id}`,
          name: playlist.name,
          type: 'playlist',
          score,
          item: playlist,
          canActivate: true // Playlists can be played
        })
      }
    }
  }
  
  // Search Containers (if filter enabled)
  if (filters.value.containers) {
    for (const container of graphContainersStore.allContainers) {
      let score = fuzzyMatch(matchQuery, container.name)
      
      // Apply type boost
      if (typeBoosts['container']) {
        score += typeBoosts['container']
      }
      
      if (score > 0) {
        results.push({
          id: container.id,
          nodeId: `container-${container.id}`,
          name: container.name,
          type: 'container',
          score,
          item: container
        })
      }
    }
  }
  
  // Sort by score (best match last so it appears closest to the input when panel is above)
  // and limit results
  return results
    .sort((a, b) => a.score - b.score)
    .slice(-15)  // Take last 15 (best matches)
})

// Reset selection when results change and fetch artwork
watch(searchResults, () => {
  // Start selection at the last item (best match, closest to input)
  selectedIndex.value = Math.max(0, searchResults.value.length - 1)
  // Fetch artwork for spotify results
  for (const result of searchResults.value) {
    if (result.type === 'spotify') {
      fetchArtwork(result.item as SpotifyNode)
    }
  }
})

const handleKeydown = (event: KeyboardEvent) => {
  if (searchResults.value.length === 0) {
    if (event.key === 'Escape') {
      event.preventDefault()
      handleBlur()
    }
    return
  }
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      // Down moves towards worse matches (lower in score, but higher index visually)
      selectedIndex.value = Math.min(selectedIndex.value + 1, searchResults.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      // Up moves towards better matches (higher in score, but lower index visually)
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (searchResults.value[selectedIndex.value]) {
        // Activate AND zoom on Enter
        activateAndZoom(searchResults.value[selectedIndex.value])
      }
      break
    case 'Escape':
      event.preventDefault()
      handleBlur()
      break
  }
}

const activateAndZoom = (result: SearchResult) => {
  emit('zoomToNode', result.nodeId)
  emit('activateItem', result)
  clearSearch()
}

const zoomToResult = (result: SearchResult) => {
  emit('zoomToNode', result.nodeId)
  clearSearch()
}

const activateResult = (result: SearchResult) => {
  emit('activateItem', result)
  clearSearch()
}

const clearSearch = () => {
  searchQuery.value = ''
  selectedIndex.value = 0
  const inputEl = inputRef.value?.$el?.querySelector('input') || inputRef.value?.$el
  inputEl?.blur()
}

const handleFocus = () => {
  isFocused.value = true
}

const handleBlur = () => {
  // Small delay to allow click events on results to fire first
  setTimeout(() => {
    isFocused.value = false
    if (!searchQuery.value) {
      searchQuery.value = ''
    }
  }, 150)
}

const openSearch = () => {
  isFocused.value = true
  // Use nextTick to ensure Vue has updated the DOM, then focus
  nextTick(() => {
    // The Input component wraps an input element, access it via $el
    const inputEl = inputRef.value?.$el?.querySelector('input') || inputRef.value?.$el
    inputEl?.focus()
  })
}

const toggleFilter = (filterKey: keyof typeof filters.value) => {
  filters.value[filterKey] = !filters.value[filterKey]
}

const getTypeIcon = (type: 'scene' | 'spotify' | 'music' | 'playlist' | 'container') => {
  switch (type) {
    case 'scene': return Layers
    case 'spotify': return Disc3
    case 'music': return Music
    case 'playlist': return ListMusic
    case 'container': return FolderOpen
  }
}

const getTypeLabel = (type: 'scene' | 'spotify' | 'music' | 'playlist' | 'container') => {
  switch (type) {
    case 'scene': return 'Scene'
    case 'spotify': return 'Spotify'
    case 'music': return 'Audio'
    case 'playlist': return 'Playlist'
    case 'container': return 'Container'
  }
}

const getArtworkUrl = (result: SearchResult): string | null => {
  if (result.type === 'spotify') {
    return artworkCache.value.get(result.id) || null
  }
  return null
}

// Expose for parent component
defineExpose({ openSearch })
</script>

<template>
  <div ref="containerRef" class="relative flex flex-col items-start">
    <!-- Results Panel (above input) -->
    <div
      v-if="isFocused && (searchResults.length > 0 || searchQuery.trim())"
      class="absolute bottom-full left-0 mb-2 w-80 bg-background border rounded-lg shadow-xl overflow-hidden"
    >
      <!-- Results List -->
      <div v-if="searchResults.length > 0" class="max-h-80 overflow-y-auto">
        <div
          v-for="(result, index) in searchResults"
          :key="`${result.type}-${result.id}`"
          :class="[
            'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors',
            index === selectedIndex ? 'bg-accent' : 'hover:bg-muted'
          ]"
          @mousedown.prevent="activateAndZoom(result)"
          @mouseenter="selectedIndex = index"
        >
          <!-- Preview Thumbnail -->
          <div class="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-muted flex items-center justify-center">
            <!-- Spotify Artwork -->
            <img 
              v-if="result.type === 'spotify' && getArtworkUrl(result)"
              :src="getArtworkUrl(result)!"
              class="w-full h-full object-cover"
              alt=""
            />
            <SpotifyIcon 
              v-else-if="result.type === 'spotify'" 
              class="h-5 w-5 text-green-500" 
            />
            <!-- Scene Icon -->
            <Layers 
              v-else-if="result.type === 'scene'" 
              class="h-5 w-5 text-primary" 
            />
            <!-- Playlist Icon (blue like music) -->
            <ListMusic 
              v-else-if="result.type === 'playlist'" 
              class="h-5 w-5 text-blue-400" 
            />
            <!-- Container Icon -->
            <FolderOpen 
              v-else-if="result.type === 'container'" 
              class="h-5 w-5 text-slate-400" 
            />
            <!-- Music Node Icon -->
            <Music 
              v-else 
              class="h-5 w-5 text-blue-400" 
            />
          </div>
          
          <!-- Info -->
          <div class="flex-1 min-w-0">
            <div class="font-medium text-sm truncate">{{ result.name }}</div>
            <div class="text-[10px] text-muted-foreground uppercase tracking-wider">
              {{ getTypeLabel(result.type) }}
            </div>
          </div>
          
          <!-- Actions -->
          <div class="flex gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              @mousedown.prevent.stop="zoomToResult(result)"
              title="Zoom only"
            >
              <MapPin class="h-3 w-3" />
            </Button>
            <Button
              v-if="result.type === 'scene' || result.type === 'playlist'"
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              @mousedown.prevent.stop="activateResult(result)"
              :title="result.type === 'scene' ? 'Activate only' : 'Play only'"
            >
              <Play class="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="searchQuery.trim()"
        class="px-3 py-4 text-center text-sm text-muted-foreground"
      >
        No results found
      </div>
      
      <!-- Filter buttons (always shown in results panel) -->
      <div class="flex justify-center gap-1 px-3 py-2 border-t border-border">
        <button
          @mousedown.prevent="toggleFilter('scenes')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.scenes 
              ? 'bg-primary/20 text-primary' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Scenes"
        >
          <Layers class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('spotify')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.spotify 
              ? 'bg-green-500/20 text-green-500' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Spotify"
        >
          <Disc3 class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('music')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.music 
              ? 'bg-orange-500/20 text-orange-500' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Audio & Playlists"
        >
          <Music class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('containers')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.containers 
              ? 'bg-slate-500/20 text-slate-400' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Containers"
        >
          <FolderOpen class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>

    <!-- Search Input (always at bottom) -->
    <div
      :class="[
        'bg-background border rounded-lg shadow-lg overflow-hidden transition-all duration-200',
        isFocused ? 'w-80' : 'w-9'
      ]"
    >
      <div class="relative flex items-center">
        <Search 
          :class="[
            'absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none transition-opacity',
            isFocused ? 'opacity-100' : 'opacity-100'
          ]" 
        />
        <Input
          ref="inputRef"
          v-model="searchQuery"
          :placeholder="isFocused ? 'Search scenes, audio...' : ''"
          :class="[
            'border-0 shadow-none focus-visible:ring-0 h-9 transition-all duration-200',
            isFocused ? 'pl-8 pr-8 w-full' : 'pl-8 pr-0 w-9 cursor-pointer'
          ]"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleKeydown"
        />
        <button
          v-if="searchQuery && isFocused"
          @mousedown.prevent="searchQuery = ''"
          class="absolute right-2 text-muted-foreground hover:text-foreground text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>

    <!-- Hint and filters (shown when focused but no query) -->
    <div
      v-if="isFocused && !searchQuery.trim() && searchResults.length === 0"
      class="absolute bottom-full left-0 mb-2 w-80 bg-background border rounded-lg shadow-xl px-3 py-3 text-xs text-muted-foreground"
    >
      <p>Type to search for scenes, Spotify playlists, or audio files.</p>
      <p class="mt-1 opacity-70">Use ↑↓ to navigate, Enter to activate & zoom</p>
      
      <!-- Filter buttons (icon only) -->
      <div class="flex justify-center gap-1 mt-2 pt-2 border-t border-border">
        <button
          @mousedown.prevent="toggleFilter('scenes')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.scenes 
              ? 'bg-primary/20 text-primary' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Scenes"
        >
          <Layers class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('spotify')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.spotify 
              ? 'bg-green-500/20 text-green-500' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Spotify"
        >
          <Disc3 class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('music')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.music 
              ? 'bg-orange-500/20 text-orange-500' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Audio & Playlists"
        >
          <Music class="h-3.5 w-3.5" />
        </button>
        <button
          @mousedown.prevent="toggleFilter('containers')"
          :class="[
            'p-1.5 rounded transition-colors',
            filters.containers 
              ? 'bg-slate-500/20 text-slate-400' 
              : 'bg-muted/50 text-muted-foreground hover:bg-muted'
          ]"
          title="Containers"
        >
          <FolderOpen class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
